"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import { Container, Section } from "@/components/ui/Container";
import { Button } from "@/components/ui/Button";
import { useDict } from "@/components/i18n/DictProvider";

// A chunk that 404s means the browser is holding HTML/JS from an older build
// than the server is now serving (version skew — happens after a deploy or, in
// dev, after a Turbopack recompile). The error boundary's reset() can't help:
// the chunk is simply gone from the server. A full reload fetches fresh HTML
// plus the current chunk graph, which is the actual recovery.
const CHUNK_RELOAD_KEY = "lh:chunkReloadAt";
const CHUNK_RELOAD_DEBOUNCE_MS = 10_000;

function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { name?: string; message?: string };
  const msg = e.message ?? "";
  return (
    e.name === "ChunkLoadError" ||
    /Loading chunk [^\s]+ failed/i.test(msg) ||
    /Failed to load chunk/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /importing a module script failed/i.test(msg)
  );
}

export function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useDict().errors;
  // Auto-recover from version skew. Debounced via sessionStorage so a chunk
  // that is genuinely missing can't trigger a reload loop; if storage is
  // unavailable we deliberately do NOT reload (show the manual UI instead)
  // rather than risk looping.
  useEffect(() => {
    if (!isChunkLoadError(error)) return;
    try {
      const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
      if (Date.now() - last < CHUNK_RELOAD_DEBOUNCE_MS) return;
      window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
      return;
    }
    window.location.reload();
  }, [error]);

  // Report to Sentry. Errors caught by a React error boundary never reach
  // window.onerror, so without this the group/listing boundaries that render
  // RouteError (the majority) would be invisible in Sentry — only the two
  // top-level boundaries capture. Skip chunk-load errors: those are version
  // skew, auto-recovered by the reload above, and would just be noise.
  useEffect(() => {
    if (isChunkLoadError(error)) return;
    Sentry.captureException(error);
  }, [error]);

  // In dev, surface the real error to the browser console so the stack trace
  // is one click away (Next 16 sanitizes server errors to a digest in prod).
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    console.error("[RouteError]", error);
  }, [error]);

  const isDev = process.env.NODE_ENV !== "production";
  return (
    <Section>
      <Container size="narrow" className="py-16">
        <div className="text-center">
          <h2>{t.generic}</h2>
          <p className="text-mid-gray mt-3">{t.genericBody}</p>
          <div className="mt-6 flex gap-3 justify-center">
            <Button onClick={reset}>{t.tryAgain}</Button>
          </div>
        </div>
        {isDev ? (
          <div className="mt-10 rounded-lg border border-red-300/60 bg-red-50/60 dark:bg-red-950/30 dark:border-red-900/60 p-4 text-left text-[12.5px] text-red-900 dark:text-red-200">
            <p className="font-display font-bold uppercase tracking-[0.18em] text-[10.5px] text-red-700 dark:text-red-300">
              Dev-only diagnostics
            </p>
            <p className="mt-2 font-mono break-words">
              <span className="opacity-70">{error.name || "Error"}: </span>
              {error.message || "(no message)"}
            </p>
            {error.digest ? (
              <p className="mt-1 font-mono opacity-80">
                <span className="opacity-70">digest: </span>
                {error.digest}
              </p>
            ) : null}
            {error.stack ? (
              <details className="mt-3">
                <summary className="cursor-pointer select-none opacity-80">stack</summary>
                <pre className="mt-2 whitespace-pre-wrap font-mono text-[11.5px] leading-relaxed opacity-90">
                  {error.stack}
                </pre>
              </details>
            ) : null}
            <p className="mt-3 text-[11px] opacity-70">
              Server-thrown errors are also logged to the dev terminal. This panel is hidden in
              production builds.
            </p>
          </div>
        ) : null}
      </Container>
    </Section>
  );
}
